import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";

import {
	$documentToken,
	type DocumentToken,
	type GetValidTokenResult,
	type PasswordExchangeResponse,
	createDocumentToken,
	getValidDocumentToken,
} from "../../internal/document-auth";
import { store } from "../../internal/store";

/**
 * Hook for managing authentication for password-protected documents.
 *
 * Provides:
 * - Token storage (in-memory only, cleared on page refresh)
 * - Automatic token refresh via oauth4webapi
 * - Error handling that triggers re-authentication on refresh failure
 *
 * @example
 * ```tsx
 * const { hasToken, setTokenFromExchange, getValidToken, clearToken } = useDocumentAuth(
 *   workspaceId,
 *   documentId
 * );
 *
 * // After password exchange
 * const result = await docsApi.document.publish.exchangePasswordForToken({ ... });
 * setTokenFromExchange(result);
 *
 * // Before making authenticated requests
 * const tokenResult = await getValidToken();
 * if (!tokenResult.success) {
 *   // Show password dialog again
 *   showPasswordDialog();
 *   return;
 * }
 * // Use tokenResult.accessToken
 * ```
 */
export const useDocumentAuth = (workspaceId: string, documentId: string) => {
	const tokenAtom = useMemo(
		() => $documentToken({ workspaceId, documentId }),
		[workspaceId, documentId]
	);

	const token = useAtomValue(tokenAtom);
	const setToken = useSetAtom(tokenAtom);

	/**
	 * Set the token from a password exchange response.
	 * Call this after successfully exchanging a password for tokens via the backend.
	 */
	const setTokenFromExchange = useCallback(
		(response: PasswordExchangeResponse) => {
			const newToken = createDocumentToken(response, workspaceId, documentId);
			setToken(newToken);
		},
		[setToken, workspaceId, documentId]
	);

	/**
	 * Get a valid access token, refreshing if necessary.
	 *
	 * Returns a result object:
	 * - `{ success: true, accessToken: string }` - Use the accessToken
	 * - `{ success: false, reason: "no_token" | "refresh_failed" }` - Show password dialog
	 *
	 * On refresh failure, the token is automatically cleared.
	 *
	 * Note: Uses store.get() to always read the latest token value,
	 * avoiding stale closure issues when token changes between calls.
	 */
	const getValidToken = useCallback(async (): Promise<GetValidTokenResult> => {
		const currentToken = store.get(tokenAtom);
		return getValidDocumentToken(currentToken, setToken);
	}, [tokenAtom, setToken]);

	/**
	 * Clear the stored token.
	 * Use this when the user needs to re-authenticate.
	 */
	const clearToken = useCallback(() => {
		setToken(null);
	}, [setToken]);

	/**
	 * Get the current access token directly (without refresh check).
	 * Useful for passing to iframe URLs where you can't await.
	 */
	const accessToken = token?.accessToken ?? null;

	return {
		/** Whether a token is currently stored */
		hasToken: token !== null,
		/** The raw token data (for advanced use cases) */
		token,
		/** The current access token (may be expired) */
		accessToken,
		/** Set token from backend password exchange response */
		setTokenFromExchange,
		/** Get a valid token, refreshing if needed. Returns result object. */
		getValidToken,
		/** Clear the stored token */
		clearToken,
	};
};

export type { DocumentToken, GetValidTokenResult, PasswordExchangeResponse };
